import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/api';
import './propertyDetails.css';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const defaultSupplyRows = () => Array.from({ length: 12 }, (_, index) => ({
    month: index + 1,
    suppliesCost: ''
}));

const mapSupplyRows = (rows) => {
    const byMonth = new Map((rows || []).map((row) => [Number(row.month), row]));
    return Array.from({ length: 12 }, (_, index) => {
        const month = index + 1;
        const row = byMonth.get(month);
        return {
            month,
            suppliesCost: row && row.suppliesCost !== undefined ? String(row.suppliesCost) : ''
        };
    });
};

const PropertySuppliesPage = () => {
    const { propertyId } = useParams();
    const navigate = useNavigate();
    const [property, setProperty] = useState(null);
    const [suppliesYear, setSuppliesYear] = useState(new Date().getFullYear());
    const [supplyRows, setSupplyRows] = useState(defaultSupplyRows());
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });

    const normalizedYear = () => {
        const parsed = parseInt(suppliesYear, 10);
        if (Number.isInteger(parsed) && parsed >= 1900 && parsed <= 3000) return parsed;
        return new Date().getFullYear();
    };

    const formatCurrency = (value) => `$${(Number(value) || 0).toFixed(2)}`;

    const loadProperty = async () => {
        const response = await api.get('/property');
        if (response && response.data) {
            const currentProperty = response.data.find((item) => item.id === parseInt(propertyId, 10));
            setProperty(currentProperty || null);
        }
    };

    const loadSupplyCharges = async () => {
        if (!propertyId) return;
        setLoading(true);
        setLoadError('');
        try {
            const response = await api.get(`/supplyCharge/property/${propertyId}?year=${suppliesYear}`);
            if (response && response.data && Array.isArray(response.data.months)) {
                setSupplyRows(mapSupplyRows(response.data.months));
            } else {
                setSupplyRows(defaultSupplyRows());
            }
        } catch (error) {
            console.error('failed to load supply charges', error);
            setSupplyRows(defaultSupplyRows());
            setLoadError('Unable to load supplies schedule for this year.');
        } finally {
            setLoading(false);
        }
    };

    const onSupplyChange = (month, value) => {
        setSupplyRows((current) => current.map((row) => {
            if (row.month !== month) return row;
            return { ...row, suppliesCost: value };
        }));
    };

    const onSupplySave = async (event) => {
        event.preventDefault();
        try {
            const year = normalizedYear();
            const payload = {
                year,
                months: supplyRows.map((row) => ({
                    month: row.month,
                    suppliesCost: parseFloat(row.suppliesCost) || 0
                }))
            };

            const response = await api.post(`/supplyCharge/property/${propertyId}`, payload);
            if (response && response.data && Array.isArray(response.data.months)) {
                setSupplyRows(mapSupplyRows(response.data.months));
                setMessage({ type: 'success', text: 'Monthly supplies charges saved successfully.' });
            }
        } catch (error) {
            console.error('failed to save supply charges', error);
            const backendMessage = error?.response?.data?.error;
            setMessage({ type: 'error', text: backendMessage || 'Failed to save supplies charges; please try again.' });
        }
    };

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, []);

    useEffect(() => {
        if (propertyId) {
            loadProperty();
            loadSupplyCharges();
        }
    }, [propertyId, suppliesYear]);

    const totalSupplies = supplyRows.reduce((sum, row) => sum + (Number(row.suppliesCost) || 0), 0);
    const averageMonthlySupplies = totalSupplies / 12;

    return (
        <div className="property-pnl-page property-utilities-page">
            <div className="property-pnl-toolbar">
                <button className="btn-secondary" onClick={() => navigate(`/PropertyDetails/${propertyId}`)}>
                    ← Back to Property Details
                </button>
                <div className="property-pnl-title-block">
                    <h2>{property?.name || 'Property'} Supplies Schedule</h2>
                    <p>{property ? `${property.address1}, ${property.city}, ${property.state}` : 'Loading property...'}</p>
                </div>
                <div className="pnl-controls">
                    <label htmlFor="suppliesYearPage">Year</label>
                    <input
                        id="suppliesYearPage"
                        type="number"
                        value={suppliesYear}
                        onChange={(event) => setSuppliesYear(event.target.value)}
                    />
                    <button type="button" className="btn-primary" onClick={loadSupplyCharges}>Reload</button>
                </div>
            </div>

            {loading && (
                <div className="app-state-card app-state-info" role="status" aria-live="polite">
                    <p>Loading supplies schedule...</p>
                </div>
            )}

            {!loading && loadError && (
                <div className="app-state-card app-state-error" role="alert">
                    <p>{loadError}</p>
                    <div>
                        <button type="button" className="btn-primary" onClick={loadSupplyCharges}>Retry</button>
                    </div>
                </div>
            )}

            {message.text && (
                <div className={`app-notice ${message.type === 'success' ? 'app-notice-success' : 'app-notice-error'}`}>
                    {message.text}
                </div>
            )}

            <div className="investment-summary-grid utility-summary-grid">
                <div className="property-pnl-card investment-summary-card">
                    <span className="investment-summary-label">Supplies Total</span>
                    <strong>{formatCurrency(totalSupplies)}</strong>
                </div>
                <div className="property-pnl-card investment-summary-card">
                    <span className="investment-summary-label">Avg Monthly Supplies</span>
                    <strong>{formatCurrency(averageMonthlySupplies)}</strong>
                </div>
                <div className="property-pnl-card investment-summary-card">
                    <span className="investment-summary-label">Default Monthly Supplies</span>
                    <strong>{formatCurrency(property?.suppliesCost)}</strong>
                </div>
            </div>

            <div className="property-pnl-card">
                <form onSubmit={onSupplySave} className="utility-page-form">
                    {loading || loadError ? (
                        <></>
                    ) : (
                        <div className="property-pnl-table-wrap">
                            <table className="utilities-table property-utility-table">
                                <thead>
                                    <tr>
                                        <th>Month</th>
                                        <th>Supplies</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {supplyRows.map((row) => (
                                        <tr key={row.month}>
                                            <td>{MONTH_LABELS[row.month - 1]}</td>
                                            <td>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={row.suppliesCost}
                                                    onChange={(event) => onSupplyChange(row.month, event.target.value)}
                                                    placeholder="0.00"
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="modal-buttons utility-page-actions">
                        <button type="submit" className="btn-primary" disabled={loading}>Save Supplies Schedule</button>
                        <button type="button" className="btn-secondary" onClick={() => navigate(`/PropertyDetails/${propertyId}`)}>Back to Property</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PropertySuppliesPage;
